/**
 * ConversationSoundService — phát âm thanh thông báo tin nhắn mới.
 *
 * Port từ FE13 `conversation.service.ts`:
 * - `alertAudioNew`: object { message, comment } — cài đặt âm thanh kiểu mới (riêng message/comment)
 * - `alertAudio`: HTMLAudioElement — cài đặt kiểu cũ (1 âm thanh chung)
 * - `playAlert(type, isMuted)`: phát âm thanh nếu chưa mute
 *
 * Audio settings được load từ `auth.settings.notifications_sounds`.
 */
import {inject, Injectable} from '@angular/core';
import {AuthService} from '@core/authentication/auth.service';
import {CONVERSATION_TYPE} from '../models/conversation.model';
import {UserSettingsService} from '@shared/services/global/user-settings.service';

/** Trạng thái bật/tắt âm thanh thông báo */
export const SOUND_STATUS = {
  ON: 1,
  OFF: 2,
} as const;

export const NOTIFICATION_SOUND: Record<string, { name: string; fileUrl: string }> = {
  'default': {name: 'Mặc định', fileUrl: 'assets/sound/default.mp3'},
  'messenger': {name: 'Messenger', fileUrl: 'assets/sound/messenger.mp3'},
  'skype': {name: 'Skype', fileUrl: 'assets/sound/skype.mp3'},
  'yahoo': {name: 'Yahoo', fileUrl: 'assets/sound/yahoo.mp3'},
  'telegram': {name: 'Telegram', fileUrl: 'assets/sound/telegram.mp3'},
  'nokia': {name: 'Nokia', fileUrl: 'assets/sound/nokia.mp3'},
  'samsung': {name: 'Samsung', fileUrl: 'assets/sound/samsung.mp3'},
  'iphone': {name: 'iPhone', fileUrl: 'assets/sound/iphone.mp3'},
};

@Injectable({providedIn: 'root'})
export class ConversationSoundService {
  private readonly auth = inject(AuthService);
  private readonly userSettings = inject(UserSettingsService);

  /** Cài đặt âm thanh kiểu mới: { message: Audio | null, comment: Audio | null } */
  private alertAudioNew: { message: HTMLAudioElement | null; comment: HTMLAudioElement | null } | null = {
    message: null,
    comment: null,
  };

  /** Cài đặt âm thanh kiểu cũ: 1 audio chung */
  private alertAudio: HTMLAudioElement | null = null;

  /** Đã load settings thành công ít nhất 1 lần — tránh ghi đè state đúng bằng state mặc định */
  private _settingsLoaded = false;

  /**
   * Khởi tạo audio settings từ thông tin user.
   * Gọi sau khi login / settings thay đổi.
   * Idempotent: có thể gọi nhiều lần, lần sau override lần trước.
   */
  public loadSettings(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const authData = (this.auth as any).auth ?? {};
    const settings = authData.settings ?? {};
    // Ưu tiên auth.settings (login response), fallback UserSettingsService (API /user/setting)
    const stSounds = settings.notifications_sounds ?? this.userSettings.setting()?.notifications_sounds;

    // ── Try new format (message/comment riêng) ──────────────────────────
    const soundSrc = stSounds as { message?: { on?: number; sound?: string }; comment?: { on?: number; sound?: string } } | undefined;
    const hasNewFormat = !!(soundSrc?.message?.on === SOUND_STATUS.ON || soundSrc?.comment?.on === SOUND_STATUS.ON);
    if (hasNewFormat) {
      // Đảm bảo alertAudioNew là object (có thể đã bị null từ lần loadSettings trước)
      if (!this.alertAudioNew) {
        this.alertAudioNew = {message: null, comment: null};
      }
      this.initAudio('message', soundSrc?.message);
      this.initAudio('comment', soundSrc?.comment);
      // Xoá audio cũ để tránh chơi cả 2
      this.alertAudio = null;
      this._settingsLoaded = true;
      return;
    }

    // ── Try old format (1 audio chung) ─────────────────────────────────
    const st = settings.notifications ?? this.userSettings.setting()?.notifications;
    if (st?.on === SOUND_STATUS.ON) {
      const url = NOTIFICATION_SOUND[st.sound]?.fileUrl;
      if (url && (!this.alertAudio || this.alertAudio.src !== url)) {
        this.alertAudio = new Audio(url);
      }
      this.alertAudioNew = null;
      this._settingsLoaded = true;
      return;
    }

    // ── Fallback: không có cài đặt nào → init default sound ──────────
    if (!this._settingsLoaded) {
      this.alertAudio = new Audio(NOTIFICATION_SOUND['default'].fileUrl);
    }
  }

  /**
   * Phát âm thanh thông báo tin nhắn mới.
   * FE13: `conversation.service.ts playAlert()`.
   *
   * @param type Loại hội thoại: CONVERSATION_TYPE.COMMENT (1) hoặc INBOX (2)
   * @param isMuted true nếu hội thoại đang tắt thông báo
   */
  public playAlert(type: number = CONVERSATION_TYPE.INBOX, isMuted: boolean = false): void {
    // Tắt thông báo hội thoại thì không bật âm thanh
    if (isMuted) return;

    if (this.alertAudioNew) {
      // Cài đặt kiểu mới — âm thanh riêng message/comment
      if (type === CONVERSATION_TYPE.COMMENT) {
        if (this.alertAudioNew.comment) {
          this.alertAudioNew.comment.play().catch(() => 0);
          return;
        }
      } else {
        if (this.alertAudioNew.message) {
          this.alertAudioNew.message.play().catch(() => 0);
          return;
        }
      }
    }

    // Cài đặt kiểu cũ — 1 âm thanh chung
    if (this.alertAudio) {
      this.alertAudio.play().catch(() => 0);
      return;
    }

    // Fallback: chưa có settings nào → init default và phát
    const defaultUrl = NOTIFICATION_SOUND['default']?.fileUrl;
    if (defaultUrl) {
      this.alertAudio = new Audio(defaultUrl);
      this.alertAudio.play().catch(() => 0);
    }
  }

  private initAudio(type: 'message' | 'comment', config: { on?: number; sound?: string } | undefined): void {
    if (config?.on === SOUND_STATUS.ON) {
      const url = NOTIFICATION_SOUND[config.sound ?? 'default']?.fileUrl;
      if (url && this.alertAudioNew) {
        if (!this.alertAudioNew[type] || this.alertAudioNew[type]?.src !== url) {
          this.alertAudioNew[type] = new Audio(url);
        }
      }
    } else if (this.alertAudioNew) {
      this.alertAudioNew[type] = null;
    }
  }
}
